import React from 'react';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, theme as antdTheme } from 'antd';
import ErrorBoundary from './ErrorBoundary';
import { AppContext } from '../contexts/AppContext';
import { CustomTheme, PublicationIdentifiers } from '../types';

export interface AppProvidersProps {
  children: React.ReactNode;
  theme: CustomTheme;
  appContextValue: {
    t: (key: string) => string;
    identifiers: PublicationIdentifiers;
  };
  onReset: () => void;
  /**
   * Forwarded to the ErrorBoundary — lets the consuming app report the
   * crash and hand back a reference id to show. See `ErrorBoundaryProps`.
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => string | undefined;
  /**
   * Whether to include the page-level ErrorBoundary. Off for providers
   * mounted inside a single panel of the experience page, where the
   * layout's own `SlotErrorBoundary` is the right granularity and a
   * full-page error `Result` would be the wrong shape for the space.
   */
  withErrorBoundary?: boolean;
  includeBorderRadius?: boolean;
  additionalTheme?: object;
}

/**
 * Shared provider wrapper that combines:
 * - AppContext.Provider (translations + identifiers)
 * - ThemeProvider (styled-components)
 * - ConfigProvider (Ant Design)
 * - ErrorBoundary
 */
export const AppProviders: React.FC<AppProvidersProps> = ({
  children,
  theme,
  appContextValue,
  onReset,
  onError,
  withErrorBoundary = true,
  includeBorderRadius = true,
  additionalTheme,
}) => {
  return (
    <AppContext.Provider value={appContextValue}>
      <ThemeProvider theme={theme as any}>
        <ConfigProvider
          theme={{
            algorithm: antdTheme.defaultAlgorithm,
            token: {
              colorPrimary: theme.colors.primary,
              ...(includeBorderRadius ? { borderRadius: 2 } : {}),
              fontFamily: theme.fontFamily,
            },
            ...additionalTheme,
          }}
        >
          {withErrorBoundary ? (
            <ErrorBoundary onReset={onReset} onError={onError}>
              {children}
            </ErrorBoundary>
          ) : (
            children
          )}
        </ConfigProvider>
      </ThemeProvider>
    </AppContext.Provider>
  );
};

/**
 * Minimal ConfigProvider wrapper for maintenance page (no AppContext/ThemeProvider needed)
 */
export const MinimalConfigProvider: React.FC<{
  children: React.ReactNode;
  fontFamily: string;
}> = ({ children, fontFamily }) => {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily,
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};
